import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import './StoreSetupPage.scss'
import merchantApi from '../../services/merchantApi'
import Header from '../../components/Header/Header'
import moment from 'moment'
import { Box, Grid } from '@mui/material'
import PhoneInputField from '../../components/PhoneInputField/PhoneInputField'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { Icon } from '@iconify/react/dist/iconify.js'

const moduleCode = 'outlet'

const DEFAULT_OUTLET_PAGE_SIZE = 20

const getPhoneParts = (value = '') => {
  const raw = String(value || '').trim()
  const match = raw.match(/^(\+\d{2,4})/)

  return {
    countryCode: match?.[1] || '+60',
    number: raw.replace(/^(\+\d{2,4})/, '').replace(/\D/g, ''),
  }
}

const normalizeOutletList = (response) => {
  const source = response?.data?.data || response?.data || {}
  const outlets = Array.isArray(source) ? source : []

  return {
    currentPage: Number(source.current_page || source.page || 1),
    totalPages: Number(source.last_page || source.total_pages || 1),
    total: Number(source.total || outlets.length || 0),
    from: Number(source.from || (outlets.length ? 1 : 0)),
    to: Number(source.to || outlets.length || 0),
    perPage: Number(source.per_page || DEFAULT_OUTLET_PAGE_SIZE),
    outlets: outlets.map((outlet) => ({
      id: outlet.id,
      code: outlet.code || outlet.outlet_ref_id || `OUTLET-${outlet.id}`,
      name: outlet.name || 'Untitled outlet',
      status: outlet.status_label || (outlet.status === 1 ? 'Open' : 'Closed'),
      address: outlet.address || '-',
      tables: Number(outlet.table_count || 0),
      maxPax: Number(outlet.max_pax || 0),
      hours: Array.isArray(outlet.service_hours)
        ? outlet.service_hours.map((hour) => ({
          day: hour.day || `Day ${hour.day_of_week}`,
          time: hour.is_closed ? 'Closed' : hour.display || `${hour.open_time || '--'} - ${hour.close_time || '--'}`,
        }))
        : [],
    })),
  }
}

function DisplayField({ label, value, badge }) {
  return (
    <div className="store-display-field">
      <span className="store-display-field__label">{label}</span>
      {badge ? (
        <div className="store-display-field__value">
          <span className="store-badge-id">{badge}</span>
        </div>
      ) : (
        <p className="store-display-field__value">{value || '-'}</p>
      )}
    </div>
  )
}

function EditMerchantDrawer({ onClose, merchantData, onSaved, stateOptions, countryOptions }) {
  const fileInputRef = useRef(null)
  const [form, setForm] = useState({
    business_name: '',
    business_registration_no: '',
    business_email: '',
    business_phone: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    postal_code: '',
    state_id: '',
    country_id: '',
    owner_name: '',
    owner_email: '',
    owner_phone: '',
  })
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [fieldError, setFieldError] = useState('')
  const [businessCountryCode, setBusinessCountryCode] = useState('+60')
  const [ownerCountryCode, setOwnerCountryCode] = useState('+60')

  useEffect(() => {
    if (!merchantData) return

    const businessPhone = parsePhoneNumberFromString(merchantData.business_phone ? `+${merchantData.business_phone}` : '')
    const ownerPhone = parsePhoneNumberFromString(merchantData.owner_phone ? `+${merchantData.owner_phone}` : '')

    setBusinessCountryCode(businessPhone?.countryCallingCode || '+60')
    setOwnerCountryCode(ownerPhone?.countryCallingCode || '+60')

    setForm({
      business_name: merchantData.business_name || '',
      business_registration_no: merchantData.business_registration_no || '',
      business_email: merchantData.business_email || '',
      business_phone: businessPhone?.nationalNumber,
      address_line_1: merchantData.address_line_1 || '',
      address_line_2: merchantData.address_line_2 || '',
      city: merchantData.city || '',
      postal_code: merchantData.postal_code || '',
      state_id: merchantData.state_id || '',
      country_id: merchantData.country_id || '',
      owner_name: merchantData.owner_name || '',
      owner_email: merchantData.owner_email || '',
      owner_phone: ownerPhone?.nationalNumber,
    })
  }, [merchantData])

  useEffect(() => {
    return () => {
      if (logoPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreviewUrl)
      }
    }
  }, [logoPreviewUrl])

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleLogoPick = (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (logoPreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreviewUrl)
    }

    setLogoFile(file)
    setLogoPreviewUrl(URL.createObjectURL(file))
    event.target.value = ''
  }

  const handleSave = async () => {
    if (submitting) return

    setSubmitting(true)
    setError('')

    try {
      const payload = {
        business_name: form.business_name?.trim(),
        business_registration_no: form.business_registration_no?.trim() || null,
        business_email: form.business_email?.trim(),
        business_phone: form.business_phone ? `${businessCountryCode}${form.business_phone?.trim()}` : null,
        address_line_1: form.address_line_1?.trim(),
        address_line_2: form.address_line_2?.trim() || null,
        city: form.city?.trim(),
        postal_code: form.postal_code?.trim() || null,
        state_id: form.state_id || merchantData?.state_id,
        country_id: form.country_id || merchantData?.country_id,
      }

      const ownerPayload = {
        name: form.owner_name?.trim(),
        email: form.owner_email?.trim(),
        phone: form.owner_phone ? `${ownerCountryCode}${form.owner_phone?.trim()}` : null,
      }

      await merchantApi.merchantProfile.update(payload)
      await merchantApi.merchantProfile.updateOwner(ownerPayload)

      if (logoFile) {
        await merchantApi.merchantProfile.updateLogo(logoFile)
      }

      await onSaved?.()
      onClose()
      window.location.reload()
    } catch (err) {
      setFieldError(err?.data?.errors || '')
      setError(err?.message || 'Unable to save merchant details.')
    } finally {
      setSubmitting(false)
      // Refresh the page to reflect changes
    }
  }

  const defaultLogoUrl = merchantData?.logo?.url || merchantData?.logo_image_url || merchantData?.logo || ''
  const previewLogoUrl = logoPreviewUrl || defaultLogoUrl

  return createPortal(
    <div className="store-drawer-overlay" onClick={onClose}>
      <div className="store-drawer" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="store-drawer__header">
          <h3>Merchant details</h3>
          <button className="store-drawer__close" onClick={onClose}>
            <Icon icon="ph:x" width={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="store-drawer__body">
          {error && <div className="sr-error">{error}</div>}
          <div className="store-drawer__section-title">BASIC INFO</div>

          {/* Business Name */}
          <div className="store-field">
            <label className="store-field__label">Business name</label>
            <input className="sr-input" type="text" value={form.business_name} onChange={(e) => handleChange('business_name', e.target.value)} />
            {fieldError?.business_name && <span className="add-outlet-field__error">{fieldError.business_name}</span>}
          </div>

          {/* Registration No */}
          <div className="store-field">
            <label className="store-field__label">
              Business registration no. <span className="store-field__optional">(optional)</span>
            </label>
            <input className="sr-input" type="text" value={form.business_registration_no} onChange={(e) => handleChange('business_registration_no', e.target.value)} />
            {fieldError?.business_registration_no && <span className="add-outlet-field__error">{fieldError.business_registration_no}</span>}
          </div>

          {/* Email */}
          <div className="store-field">
            <label className="store-field__label">Business email address</label>
            <input className="sr-input" type="email" value={form.business_email} onChange={(e) => handleChange('business_email', e.target.value)} />
            {fieldError?.business_email && <span className="add-outlet-field__error">{fieldError.business_email}</span>}
          </div>

          {/* Phone Number */}
          <div className="store-field">
            <label className="store-field__label">Business phone number</label>
            {/* <div className="sr-phone-input">
              <span className="sr-phone-input__prefix">+60</span>
              <input type="text" value={form.business_phone} onChange={(e) => handleChange('business_phone', e.target.value)} />
            </div> */}
            <PhoneInputField
              value={form.business_phone}
              onChange={(value) => handleChange('business_phone', value)}
              countryCode={businessCountryCode}
              onCountryCodeChange={setBusinessCountryCode}
            />
            {fieldError?.business_phone && <span className="add-outlet-field__error">{fieldError.business_phone}</span>}
          </div>

          {/* Logo Upload */}
          <div className="store-field">
            <label className="store-field__label">
              Merchant logo <span className="store-field__optional">(optional)</span>
            </label>
            <div className="store-logo-inline">
              <div className="store-logo-inline__preview">
                {previewLogoUrl ? (
                  <img src={previewLogoUrl} alt="Merchant logo" className="mock-logo-circle" />
                ) : (
                  <div className="mock-logo-circle">
                    <span style={{ fontSize: '18px', color: '#004348', fontWeight: 'bold' }}>H</span>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={handleLogoPick}
              />
              <button
                className="store-btn store-btn--outline store-btn--sm"
                type="button"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload
              </button>
            </div>
          </div>

          {/* Address Line 1 */}
          <div className="store-field">
            <label className="store-field__label">Address line 1</label>
            <input className="sr-input" type="text" value={form.address_line_1} onChange={(e) => handleChange('address_line_1', e.target.value)} />
            {fieldError?.address_line_1 && <span className="add-outlet-field__error">{fieldError.address_line_1}</span>}
          </div>

          {/* Address Line 2 */}
          <div className="store-field">
            <label className="store-field__label">
              Address line 2 <span className="store-field__optional">(optional)</span>
            </label>
            <input className="sr-input" type="text" placeholder="e.g. Apt / Suite" value={form.address_line_2} onChange={(e) => handleChange('address_line_2', e.target.value)} />
            {fieldError?.address_line_2 && <span className="add-outlet-field__error">{fieldError.address_line_2}</span>}
          </div>

          {/* City */}
          <div className="store-field">
            <label className="store-field__label">City</label>
            <input className="sr-input" type="text" value={form.city} onChange={(e) => handleChange('city', e.target.value)} />
            {fieldError?.city && <span className="add-outlet-field__error">{fieldError.city}</span>}
          </div>

          <div className="store-field">
            <label className="store-field__label">Postal code</label>
            <input className="sr-input" type="text" value={form.postal_code} onChange={(e) => handleChange('postal_code', e.target.value)} />
            {fieldError?.postal_code && <span className="add-outlet-field__error">{fieldError.postal_code}</span>}
          </div>

          {/* State */}
          <div className="store-field">
            <label className="store-field__label">State</label>
            <div className="store-select">
              <select value={form.state_id || ''} onChange={(e) => handleChange('state_id', e.target.value)} placeholder="Select state">
                <option key={'none'} value={''}>Select state</option>
                {
                  stateOptions?.map((state) => (
                    <option key={state.id} value={state.id}>{state.name}</option>
                  ))
                }
              </select>
              <Icon icon="ph:caret-down" width={16} className="store-select__icon" />
              {fieldError?.state_id && <span className="add-outlet-field__error">{fieldError.state_id}</span>}
            </div>
          </div>
          {/* Country */}
          <div className="store-field">
            <label className="store-field__label">Country</label>
            <div className="store-select store-select__disabled">
              <select value={form.country_id || ''} onChange={(e) => handleChange('country_id', e.target.value)} placeholder="Select country" disabled={true}>
                <option key={'none'} value={''}>Select country</option>
                {
                  countryOptions?.map((country) => (
                    <option key={country.id} value={country.id}>{country.name}</option>
                  ))
                }
              </select>
              <Icon icon="ph:caret-down" width={16} className="store-select__icon" />
              {fieldError?.country_id && <span className="add-outlet-field__error">{fieldError.country_id}</span>}
            </div>
          </div>
          <div className="store-drawer__section-title">OWNER INFORMATION</div>
          {/* Name */}
          <div className="store-field">
            <label className="store-field__label">Name</label>
            <input className="sr-input" type="text" value={form.owner_name} onChange={(e) => handleChange('owner_name', e.target.value)} />
            {fieldError?.name && <span className="add-outlet-field__error">{fieldError.name}</span>}
          </div>
          {/* Phone Number */}
          <div className="store-field">
            <label className="store-field__label">Phone number</label>
            <PhoneInputField
              value={form.owner_phone}
              onChange={(value) => handleChange('owner_phone', value)}
              countryCode={ownerCountryCode}
              onCountryCodeChange={setOwnerCountryCode}
            />
            {fieldError?.phone && <span className="add-outlet-field__error">{fieldError.phone}</span>}
          </div>
          {/* Email */}
          <div className="store-field">
            <label className="store-field__label">Email</label>
            <input className="sr-input" type="email" value={form.owner_email} onChange={(e) => handleChange('owner_email', e.target.value)} />
            {fieldError?.email && <span className="add-outlet-field__error">{fieldError.email}</span>}
          </div>
        </div>

        {/* Footer */}
        <div className="store-drawer__footer">
          <button className="store-btn store-btn--ghost" type="button" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="store-btn store-btn--dark store-btn--lg" type="button" onClick={handleSave} disabled={submitting}>{submitting ? 'Saving...' : 'Save changes'}</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Tab 1: Overview ──────────────────────────────────────────────────────────
function OverviewTab({ onEditMerchant, merchantData }) {
  const [timeout, setTimeout] = useState('')
  const [kioskLoading, setKioskLoading] = useState(true)
  const [kioskSaving, setKioskSaving] = useState(false)
  const [kioskError, setKioskError] = useState('')
  const [kioskNotice, setKioskNotice] = useState('')

  useEffect(() => {
    let mounted = true

    const loadKioskSetting = async () => {
      setKioskLoading(true)
      setKioskError('')

      try {
        const response = await merchantApi.kioskSetting.show()
        const setting = response?.data?.data || response?.data || response

        if (mounted) {
          setTimeout(setting?.session_timeout_minutes ?? '')
        }
      } catch (error) {
        if (mounted) {
          setKioskError(error?.message || 'Unable to load kiosk timeout.')
        }
      } finally {
        if (mounted) {
          setKioskLoading(false)
        }
      }
    }

    loadKioskSetting()

    return () => {
      mounted = false
    }
  }, [])

  const handleSaveTimeout = async () => {
    const parsedTimeout = Number(timeout)

    if (timeout === '' || !Number.isInteger(parsedTimeout) || parsedTimeout < 0) {
      setKioskError('Kiosk timeout must be a whole number of minutes.')
      setKioskNotice('')
      return
    }

    setKioskSaving(true)
    setKioskError('')
    setKioskNotice('')

    try {
      const payload = {
        session_timeout_minutes: parsedTimeout,
      }
      const response = await merchantApi.kioskSetting.update(payload)
      const setting = response?.data?.data || response?.data

      setTimeout(setting?.session_timeout_minutes ?? parsedTimeout)
      setKioskNotice(response?.message || 'Kiosk timeout updated successfully.')
      setKioskError('')
    } catch (error) {
      const validationError = error?.data?.errors?.session_timeout_minutes
      setKioskError(
        (Array.isArray(validationError) ? validationError[0] : validationError)
        || error?.message
        || 'Unable to save kiosk timeout.'
      )
    } finally {
      setKioskSaving(false)
    }
  }

  return (
    <>
      <div className="store-subcontainer">
        <div className="store-section-header">
          <h2>Merchant Information</h2>
          <button className="store-btn store-btn--dark" onClick={onEditMerchant}>
            Edit merchant
          </button>
        </div>
        <Grid container spacing={2}>
          <Grid item lg={6} md={12} sm={12} xs={12}>
            <div className="store-card">
              <div className="store-card__header">Business Details</div>
              <div className="store-card__body store-grid-2-inner">
                <DisplayField label="Merchant ID" badge={merchantData?.merchant_code} />
                <DisplayField label="Business name" value={merchantData?.business_name} />
                <DisplayField label="Business registration no." value={merchantData?.business_registration_no} />
                <DisplayField label="Business email" value={merchantData?.business_email} />
                <DisplayField label="Business phone number" value={`${merchantData?.business_phone || ''}`} />
                <DisplayField label="Address" value={`${merchantData?.address_line_1 || ''}, ${merchantData?.address_line_2 || ''}`} />
              </div>
            </div>
          </Grid>
          <Grid item lg={6} md={12} sm={12} xs={12}>
            <div className="store-card">
              <div className="store-card__header">Owner Information</div>
              <div className="store-card__body store-grid-2-inner">
                <DisplayField label="Owner" value={merchantData?.owner_name || '-'} />
                <DisplayField label="Email" value={merchantData?.owner_email || '-'} />
                <DisplayField label="Phone number" value={`${merchantData?.owner_phone || '-'}`} />
                <DisplayField label="Member since" value={merchantData?.created_at ? moment(merchantData?.created_at).format('MMMM D, YYYY') : '-'} />
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
      <Box className="store-subcontainer" my={5}>
        <div className="store-section-header">
          <Box mb={1}>
            <h2>Kiosk Timeout Config</h2>
            <p className="store-display-field__label">Define how long should the kiosk keep customer's ordering sessions</p>
          </Box>
          <button
            className="store-btn store-btn--dark"
            type="button"
            onClick={handleSaveTimeout}
            disabled={kioskLoading || kioskSaving}
          >
            {kioskSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
        {kioskNotice && <div className="store-kiosk-message store-kiosk-message--success">{kioskNotice}</div>}
        {kioskError && <div className="store-kiosk-message store-kiosk-message--error">{kioskError}</div>}
        <Grid container spacing={2}>
          <Grid item sm={6} className="">
            <div className="store-field">
              <label className="store-field__label">Kiosk Timeout (minutes)</label>
              <div>
                <input
                  className={"sr-input"}
                  type="number"
                  value={timeout}
                  min="0"
                  step="1"
                  disabled={kioskLoading || kioskSaving}
                  onChange={(e) => {
                    setTimeout(e.target.value)
                    setKioskError('')
                    setKioskNotice('')
                  }}
                  placeholder='0'
                />
                {kioskLoading && <span className="store-field__hint">Loading kiosk setting...</span>}
              </div>
            </div>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

// ── Tab 2: Outlets ───────────────────────────────────────────────────────────
function OutletsTab({ onAddOutlet, handlePageChange, currentPage, totalPages, outletList, navigate, loading, editable }) {
  if (loading) {
    return (
      <div className="store-subcontainer">
        <div className="store-loading">Loading outlets...</div>
      </div>
    )
  }

  const outlets = outletList?.outlets || []
  const from = outletList?.from || (outlets.length ? ((currentPage - 1) * (outletList?.perPage || DEFAULT_OUTLET_PAGE_SIZE)) + 1 : 0)
  const to = outletList?.to || (outlets.length ? from + outlets.length - 1 : 0)
  const total = outletList?.total || outlets.length

  const getPaginationPages = () => {
    if (totalPages <= 6) return Array.from({ length: totalPages }, (_, i) => i + 1)

    if (currentPage <= 4) return [1, 2, 3, 4, 5, 'ellipsis', totalPages]
    if (currentPage >= totalPages - 3) return [1, 'ellipsis', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]

    return [1, 'ellipsis-start', currentPage - 1, currentPage, currentPage + 1, 'ellipsis-end', totalPages]
  }

  return (
    <div className="store-subcontainer">
      <div className="store-section-header">
        <h2>Outlets</h2>
        {/* <button className="store-btn store-btn--dark" onClick={onAddOutlet}>Add outlet</button> */}
      </div>

      <div className="store-outlet-list">
        {outlets.map((outlet) => (
          <div key={outlet.id} className="store-card store-outlet-card">
            <div className="store-outlet-card__header">
              <div className="store-outlet-card__title-area">
                <span className="store-badge-id">{outlet.code || outlet.id}</span>
                <h3>{outlet.name}</h3>
              </div>

              {editable && (
                <button className="store-btn store-btn--outline-sm" onClick={() => navigate(`/outlet-setup/edit/${outlet.id}`)}>Edit</button>
              )}
            </div>

            <div className="store-outlet-card__body">
              <div className="store-grid-2-inner">
                <div className="store-display-field">
                  <span className="store-display-field__label">Status</span>
                  <div className="store-badge-status"><span className="dot" />{outlet.status}</div>
                </div>
                <DisplayField label="Address" value={outlet.address} />
                <DisplayField label="Table" value={outlet.tables || 0} />
                <DisplayField label="Max pax" value={outlet.maxPax || 0} />
              </div>

              <div className="store-outlet-hours">
                <p className="store-outlet-hours__label">Service hours</p>
                <div className="store-hours-grid">
                  {outlet.hours.map((h, index) => (
                    <div key={index} className="store-hours-bubble">
                      <span className="store-hours-bubble__day">{h.day}</span>
                      <span className="store-hours-bubble__time">{h.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* PAGINATION */}
      <div className="orders-pagination">
        <div>
          Showing {from}-{to} of {total} results
        </div>

        <div className="orders-pagination__pages">
          <button
            type="button"
            className="orders-pagination__arrow"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            aria-label="Previous page"
          >
            ‹
          </button>

          {getPaginationPages().map((page, index) => (
            typeof page === 'string' ? (
              <span key={`${page}-${index}`} className="orders-pagination__ellipsis">...</span>
            ) : (
              <button
                key={page}
                type="button"
                className={currentPage === page ? 'active' : ''}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            )
          ))}

          <button
            type="button"
            className="orders-pagination__arrow"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  )
}

export default function StoreSetupPage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [isEditMerchantOpen, setIsEditMerchantOpen] = useState(false)
  const [outletList, setOutletList] = useState(null)
  const [userInfo, setUserInfo] = useState(null)
  const [notice, setNotice] = useState(null)
  const [pageError, setPageError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_OUTLET_PAGE_SIZE)
  const [merchantData, setMerchantData] = useState(null)
  const [stateOptions, setStateOptions] = useState(null)
  const [countryOptions, setCountryOptions] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [outletLoading, setOutletLoading] = useState(false)
  // permission states
  const [editable, setEditable] = useState(true)

  const navigate = useNavigate()

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return
    setCurrentPage(page)
  }

  useEffect(() => {
    let mounted = true

    const loadInitialData = async () => {
      setInitialLoading(true)
      await Promise.all([loadUserData(), loadMerchantData()])
      if (mounted) {
        setInitialLoading(false)
      }
    }

    loadInitialData()

    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    loadOutletData(currentPage)
  }, [currentPage])

  const loadOutletData = async (page = currentPage) => {
    setOutletLoading(true)
    try {
      const response = await merchantApi.outlets.list({
        size: pageSize,
        page,
        currentPage: page,
      })
      const normalized = normalizeOutletList(response)
      setOutletList(normalized)
      setPageSize(normalized.perPage || DEFAULT_OUTLET_PAGE_SIZE)
      setTotalPages(normalized.totalPages || 1)
    } catch (error) {
      setPageError(error.message || 'Error fetching data.')
    } finally {
      setOutletLoading(false)
    }
  }

  const loadUserData = async () => {
    try {
      const response = await merchantApi.auth.me();
      if (!response?.data?.permissions?.includes(`view-${moduleCode}`)) {
        navigate('/unauthorized')
        return
      }
      const generalData = await merchantApi.general.systemInfo()
      setUserInfo(response?.data || null)
      setStateOptions(generalData?.data?.states || [])
      setCountryOptions(generalData?.data?.countries || [])
      setEditable(response?.data?.permissions?.includes(`edit-${moduleCode}`))
    } catch (error) {
      setPageError(error.message || 'Error fetching data.')
    }
  }

  const loadMerchantData = async () => {
    try {
      const response = await merchantApi.merchantProfile.show();
      setMerchantData(response?.data || null)
      pageError && setPageError(null)
    } catch (error) {
      setPageError(error.message || 'Error fetching data.')
    }
  }

  const handleMerchantSaved = async () => {
    await loadMerchantData()
    setNotice('Merchant details updated successfully.')
  }

  return (
    <div className="store-page">
      <Header
        items={[
          { label: userInfo?.merchant?.business_name || 'Merchant', to: '/' },
          { label: 'Store setup', current: true },
        ]}
      />
      <div className="store-page__topbar">
        <div>
          <h1 className="store-page__title">Store setup</h1>
          <p className="store-page__subtitle">Store information and outlet information</p>
        </div>
      </div>

      {notice && <div className="sr-success">{notice}</div>}
      {pageError && <div className="sr-error">{pageError}</div>}

      <div className="store-tabs">
        <button
          className={`store-tab ${activeTab === 'overview' ? 'store-tab--active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`store-tab ${activeTab === 'outlets' ? 'store-tab--active' : ''}`}
          onClick={() => setActiveTab('outlets')}
        >
          Outlets
        </button>
      </div>

      {initialLoading ? (
        <div className="store-subcontainer">
          <div className="store-loading">Loading store setup...</div>
        </div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <OverviewTab merchantData={merchantData} onEditMerchant={() => setIsEditMerchantOpen(true)} />
          )}
          {activeTab === 'outlets' && (
            <OutletsTab
              onAddOutlet={() => navigate('/outlet-setup/add')}
              outletList={outletList}
              handlePageChange={handlePageChange}
              currentPage={currentPage}
              totalPages={totalPages}
              navigate={navigate}
              loading={outletLoading}
              editable={editable}
            />
          )}
        </>
      )}

      {isEditMerchantOpen && (
        <EditMerchantDrawer
          merchantData={merchantData}
          stateOptions={stateOptions}
          countryOptions={countryOptions}
          onSaved={handleMerchantSaved}
          onClose={() => setIsEditMerchantOpen(false)}
        />
      )}
    </div>
  )
}
